import {
  AutoFillFormTypeEnum,
  GeneratedImageToolResult,
  SetupGuideAiModeEnum,
} from 'libs/gb-dashboard/shared/data-access-ai/src/lib/+state/ai.models'
import {
  GbLocalStorageService,
  getAiChatHistoryKey,
} from 'libs/gb-dashboard/shared/utils/src/lib/gb-localstorage-service/gb-localstorage.service'
import { SetupGuideAiChatSession, SetupGuideAiMessage } from '../types/ai-chat.types'
import { generateChatMessageId } from './ai-message-history.util'

const IMAGE_TOOL_NAMES = new Set(['generate_logo', 'generate_banner'])

function isImageToolContent(toolContent: SetupGuideAiMessage['toolContent']): toolContent is GeneratedImageToolResult {
  return !!toolContent && typeof toolContent === 'object' && 'base64_image' in toolContent
}

function stripBase64FromMessages(messages: SetupGuideAiMessage[]): SetupGuideAiMessage[] {
  return messages.map((message) => {
    if (!message.toolContent || !message.toolName || !IMAGE_TOOL_NAMES.has(message.toolName)) {
      return message
    }
    if (!isImageToolContent(message.toolContent)) {
      return message
    }
    const { base64_image, ...rest } = message.toolContent
    return { ...message, toolContent: { ...rest } as GeneratedImageToolResult }
  })
}

export class AiChatSessionUtil {
  private sessions: SetupGuideAiChatSession[] = []
  private currentSessionId: string | null = null
  private auctionUrlName = ''
  private currentSearchValue: string | null = null
  filteredSessions: SetupGuideAiChatSession[] = []
  selectedSessionId: string | null = null

  constructor(private gbLocalStorageService: GbLocalStorageService) {}

  setSearchValue(value: string | null): void {
    this.currentSearchValue = value
    this.filterSessions(value)
  }

  get currentAuctionUrlName(): string {
    return this.auctionUrlName
  }

  get currentSession(): string | null {
    return this.currentSessionId
  }

  loadSessionsForAuction(urlName: string): void {
    this.auctionUrlName = urlName
    this.sessions = this.gbLocalStorageService.getItem<SetupGuideAiChatSession[]>(getAiChatHistoryKey(urlName)) || []
    this.filteredSessions = []
    this.currentSessionId = null
    this.selectedSessionId = null
  }

  filterSessions(searchValue: string | null): void {
    const query = (searchValue || '').toLowerCase().trim()
    this.filteredSessions = this.sessions.filter((session) => {
      if (!query) return true

      const titleMatches = session.title.toLowerCase().includes(query)
      const textMatches = session.messages.some((message) => message.content.toLowerCase().includes(query))

      return titleMatches || textMatches
    })
  }

  ensureSession(formType: AutoFillFormTypeEnum | null | undefined, prompt: string, aiMode: SetupGuideAiModeEnum): void {
    if (this.currentSessionId) {
      return
    }

    const now = new Date().toISOString()
    const title = prompt.length > 60 ? `${prompt.slice(0, 60)}...` : prompt
    const session: SetupGuideAiChatSession = {
      id: generateChatMessageId(),
      title,
      formType,
      aiMode,
      auctionUrlName: this.auctionUrlName,
      createdAt: now,
      updatedAt: now,
      messages: [],
    }

    this.currentSessionId = session.id
    this.selectedSessionId = session.id
    this.sessions = [session, ...this.sessions]
    this.saveSessions()
    this.filterSessions(this.currentSearchValue)
  }

  startNewSession(): void {
    this.currentSessionId = null
    this.selectedSessionId = null
  }

  loadSession(sessionId: string): SetupGuideAiMessage[] | null {
    const session = this.sessions.find((chatSession) => chatSession.id === sessionId)
    if (!session) {
      return null
    }

    this.selectedSessionId = session.id
    this.currentSessionId = session.id
    return [...session.messages]
  }

  updateCurrentSessionMessages(messages: SetupGuideAiMessage[]): void {
    if (!this.currentSessionId) {
      return
    }

    this.sessions = this.sessions.map((session) => {
      if (session.id !== this.currentSessionId) {
        return session
      }

      return {
        ...session,
        updatedAt: new Date().toISOString(),
        messages: [...messages],
      }
    })

    this.sessions = this.sessions.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    this.saveSessions()
    this.filterSessions(this.currentSearchValue)
  }

  getCurrentSessionFormType(): AutoFillFormTypeEnum | null | undefined {
    if (!this.currentSessionId) return undefined
    return this.sessions.find((session) => session.id === this.currentSessionId)?.formType
  }

  getCurrentSessionAiMode(): SetupGuideAiModeEnum | undefined {
    if (!this.currentSessionId) return undefined
    return this.sessions.find((session) => session.id === this.currentSessionId)?.aiMode
  }

  updateCurrentSessionAiMode(aiMode: SetupGuideAiModeEnum): void {
    if (!this.currentSessionId) {
      return
    }

    this.sessions = this.sessions.map((session) =>
      session.id === this.currentSessionId ? { ...session, aiMode } : session,
    )
    this.saveSessions()
  }

  private saveSessions(): void {
    const sessionsToSave = this.sessions.map((session) => ({
      ...session,
      messages: stripBase64FromMessages(session.messages),
    }))
    this.gbLocalStorageService.setItem(getAiChatHistoryKey(this.auctionUrlName), sessionsToSave)
  }
}
